import type { SiteStatus, SiteSuspension, SlotRow, ToolReview } from "../../api/types";
import type { Tone } from "../../components/ui";

// UX-DESIGN.md §3.1 status and suspension vocabulary. The status shown is
// the site's effective_status (publisher_reads.go:239-248): sites.status
// itself never becomes suspended_*, a suspension row does.

export const SITE_STATUS: Record<SiteStatus, { tone: Tone; label: string; detail: string }> = {
  pending_review: {
    tone: "warning",
    label: "Pending verification",
    detail: "Prove domain control below. Until the site is active, live API keys can't be created.",
  },
  active: { tone: "success", label: "Active", detail: "Domain control is proven. You can create API keys and serve ads." },
  suspended_safety: {
    tone: "error",
    label: "Suspended — safety",
    detail: "Paid serving and house ads are stopped for this site. A safety suspension needs platform review to lift; it can't be resolved from here.",
  },
  suspended_ordinary: {
    tone: "warning",
    label: "Suspended",
    detail: "New CPE reservations are stopped for this site. CPM serving, and earnings already captured, continue.",
  },
};

/** What a suspension blocks (suspensionView, publisher_reads.go:146-178), in plain language. */
export const BLOCKS: Record<string, string> = {
  reservations: "new ad reservations",
  capture: "capturing earnings",
  recognition: "recognizing earnings",
  payout: "paying out earnings from this site",
  cpe_reservations: "new CPE reservations",
};

export const REASON_CATEGORY: Record<SiteSuspension["reason_category"], string> = {
  safety_review: "Safety review",
  traffic_anomaly: "Unusual traffic",
  ordinary_review: "Under review",
};

export function remediationText(s: SiteSuspension): string {
  switch (s.remediation) {
    case "platform_review_required":
      return "This needs platform review to lift. It can't be resolved from here; contact support if you have information for the review.";
    case "wait_for_cooldown":
      return s.auto_lift_after
        ? `It lifts automatically after a cooldown, at ${new Date(s.auto_lift_after).toLocaleString()} unless the traffic pattern continues.`
        : "It lifts automatically after a cooldown.";
    case "contact_support":
      return "Contact support to find out what's needed to lift it.";
  }
}

export const SLOT_STATUS: Record<SlotRow["status"], { tone: Tone; label: string }> = {
  active: { tone: "success", label: "Active" },
  inactive: { tone: "neutral", label: "No live version" },
  suspended_safety: { tone: "error", label: "Suspended — safety" },
  suspended_ordinary: { tone: "warning", label: "Suspended" },
  paused: { tone: "neutral", label: "Paused" },
  retired: { tone: "neutral", label: "Retired" },
};

/** Migration 0066: why a slot isn't serving today, in plain language (GET /v1/sites/{id}/slots' serving_blocked_reason). */
export const SERVING_BLOCKED_REASON: Record<string, string> = {
  slot_paused: "This slot is paused.",
  slot_retired: "This slot was retired.",
  slot_version_not_approved: "This slot's live version hasn't been approved yet.",
  tool_set_not_approved: "This site's declared tools haven't been approved yet.",
};

/** Migration 0066: the tool-set review status (G-33), in plain language. */
export const TOOL_REVIEW_STATUS: Record<ToolReview["status"], { tone: Tone; label: string }> = {
  not_declared: { tone: "warning", label: "Tools not declared" },
  pending_review: { tone: "warning", label: "Pending review" },
  approved: { tone: "success", label: "Approved" },
  changes_pending_review: { tone: "warning", label: "Changes pending review" },
  rejected: { tone: "error", label: "Rejected" },
};

/** What serving uses today, per ToolReview.serving_uses. */
export function servingUsesText(review: ToolReview): string {
  switch (review.serving_uses) {
    case "approved_declaration":
      return "Paid ads serve from the approved declared tool set.";
    case "grandfathered_domain_approval":
      return "This site predates tool-set review: it serves on domain approval alone. Declaring tools starts review going forward.";
    case "none":
      return "Nothing serves yet: declare this site's tools and wait for approval.";
  }
}
